import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { ATTACHMENT_MAX_SIZE } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import useAiFileExtraction, {
  type UseAiFileExtractionOptions,
} from '@/hooks/useAiFileExtraction';
import AiFileUploadIcon from '@/public/icons/approval/ai-file-upload.svg';
import { AiSpinner } from './styles';

const Row = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
});

/** 레거시 .show-file-name — 선택 전엔 안내 문구를 흐리게 */
const FileNameBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'empty',
})<{ empty: boolean }>(({ empty }) => ({
  width: 316,
  maxWidth: '100%',
  height: 44,
  boxSizing: 'border-box',
  padding: 12,
  border: '1px solid var(--dk-line, #E7E9EC)',
  borderRadius: 2,
  backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
  fontSize: 14,
  lineHeight: '20px',
  color: empty ? 'var(--dk-text-disabled, #D1D8E2)' : 'var(--dk-text, #1A1A1A)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const LoadingLabel = styled('span')({
  color: '#c77ac4',
});

const ResultSlot = styled(Box)({
  marginTop: 5,
});

const SmallSpinner = styled(AiSpinner)({
  width: 16,
  height: 16,
});

export type AiFileSelectFieldProps<TResult> =
  UseAiFileExtractionOptions<TResult> & {
    /** 상자에 보일 파일명. 비면 placeholder */
    fileName?: string;
    placeholder?: string;
    allowedExtensions: string[];
    /** 기본 500MB(레거시 AI 확인 업로더 기준) */
    maxSize?: number;
    /** 상자 아래 결과 표시(확인 완료 문구 등) */
    result?: ReactNode;
    disabled?: boolean;
  };

const extensionOf = (name: string) =>
  (name.split('.').pop() ?? '').toLowerCase();

/**
 * AI 파일 확인 — 파일명 상자 + 파일 선택 버튼(레거시 approval_ai_verifier).
 * 업로드·분석은 useAiFileExtraction, 분석 중엔 버튼이 'AI 검토 중'으로 잠김
 */
const AiFileSelectField = <TResult,>({
  fileName,
  placeholder,
  allowedExtensions,
  maxSize = ATTACHMENT_MAX_SIZE,
  result,
  disabled,
  ...options
}: AiFileSelectFieldProps<TResult>) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const { start, isProcessing } = useAiFileExtraction(options);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length || isProcessing) return;
    const fail = (message: string) => snackbar.showError({ message });
    if (files.length > 1) {
      fail(t('첨부파일은 한번에 1개만 등록할 수 있습니다.'));
      return;
    }
    const [file] = files;
    if (file.size === 0) {
      fail(t('0MB 파일은 업로드할 수 없습니다.'));
      return;
    }
    if (file.size > maxSize) {
      fail(
        t('파일 용량이 너무 큽니다. 최대 {{mb}}MB까지 업로드할 수 있습니다.', {
          mb: Math.floor(maxSize / 1024 / 1024),
        }),
      );
      return;
    }
    if (!allowedExtensions.includes(extensionOf(file.name))) {
      fail(t('해당 파일 형식은 지원되지 않습니다.'));
      return;
    }
    start(file);
  };

  return (
    <Box>
      <Row>
        <FileNameBox empty={!fileName}>
          {fileName || placeholder || t('파일을 선택해 주세요')}
        </FileNameBox>
        <BoxButton
          variant="secondary"
          size="medium"
          // 분석 중 다른 파일을 고르면 늦게 끝난 이전 업로드가 새 파일 결과를 덮어씀 — 키보드 선택까지 막음
          disabled={disabled || isProcessing}
          aria-busy={isProcessing}
          startIcon={
            isProcessing ? (
              <SmallSpinner />
            ) : (
              <AiFileUploadIcon width={18} height={18} />
            )
          }
          onClick={() => {
            if (!isProcessing) inputRef.current?.click();
          }}
        >
          {isProcessing ? (
            <LoadingLabel>{t('AI 검토 중')}</LoadingLabel>
          ) : (
            t('파일 선택')
          )}
        </BoxButton>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
          onChange={handleChange}
        />
      </Row>
      {result && <ResultSlot>{result}</ResultSlot>}
    </Box>
  );
};

export default AiFileSelectField;
